import { useAuth } from '@agentnet/auth';
import { DataTable, pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ColDef, ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { MarketplaceProduct } from 'api/admin/types';
import { getMarketplaceOrderReport } from 'api/orderInsights/order-insights-api';
import { DocumentType, KBLinks, newWindow } from 'features/constants';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import { WIRE_VALIDATION_PRODUCT_CODE, WIRE_VALIDATION_SUBTITLE } from 'features/help/constants';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import CopyCreateIcon from 'ui-kit/icons/CopyCreateIcon';
import PDFIcon from 'ui-kit/icons/PDF';
import { syncNoRowsOverlay } from 'utilities/ag-grid/grid-utility';
import { WIRE_VALIDATION } from 'utilities/constants/activity-rights';
import { getApiErrorMessages, openDocument } from 'utilities/utilities';
import VerificationChip from 'features/services/identityVerification/VerificationChip';
import { WireValidationRow } from './interfaces/wireValidation';
import { normalizeWireValidationRows, wireValidationColDefs } from './wireValidation.config';
import WireVerificationDrawer from './WireVerificationDrawer';
import WireVerificationEmptyState from './WireVerificationEmptyState';

interface ActionsCellRendererProps {
  data: WireValidationRow;
  loadingReportFileId: string | null;
  loadingReportOrderId: string | null;
  onReportClick: (fileId: string, orderId: string, payeeOrBankName: string) => void;
  node: IRowNode<WireValidationRow>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: pxToRem(114),
    },
    contentWrap: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: theme.spacing(0, 3, 3),
      paddingBottom: 0,
    },
    gridContainer: {
      fontFamily: theme.typography.fontFamily,
      height: 'calc(100vh - 287px) !important',
      marginTop: theme.spacing(2),
      width: '100%',
    },
    searchWrap: {
      display: 'flex',
      '& form': {
        marginBlockEnd: 'auto',
      },
    },
    searchButton: {
      height: '30px',
    },
    actionsCell: {
      alignItems: 'center',
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
    },
    actionSlot: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      width: pxToRem(32),
    },
  }),
);

const WireVerification = () => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const marketPlace = useMarketPlace();
  const {
    fetchWireValidationList,
    fetchWireValidationListResults,
    fetchWireValidationListStatus,
    fetchMarketPlaceServices,
    fetchMarketPlaceServicesResults,
    doSubmitWireValidation,
    submitWireValidationStatus,
    submitWireValidationResult,
    resetSubmitWireValidation,
    getWireValidationOrderDetail,
    getWireValidationOrderDetailResults,
    getWireValidationOrderDetailStatus,
  } = marketPlace;

  const gridRef = useRef<AgGridReact>(null);
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile, userFirm } = profileCtx;
  const { addGlobalMsg } = useGlobalMessages();

  const [displayedRowCount, setDisplayedRowCount] = useState(0);
  const [rowData, setRowData] = useState<WireValidationRow[]>([]);
  const [isWireValidationEnabled, setIsWireValidationEnabled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerPrefilledValue, setDrawerPrefilledValue] = useState<Record<string, unknown> | undefined>(undefined);
  const [showOrderDetailResult, setShowOrderDetailResult] = useState(false);
  const [loadingReportFileId, setLoadingReportFileId] = useState<string | null>(null);
  const [loadingReportOrderId, setLoadingReportOrderId] = useState<string | null>(null);
  const orderDetailIntentRef = useRef<'view' | 'copyCreate'>('view');
  const hasSubmittedRef = useRef(false);
  const agentNetAdmin = profile?.activityRights?.some((right) => right.ActivityRightId === 36);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
      flex: 1,
      editable: false,
      suppressMenu: true,
    }),
    [],
  );

  const hasWireValidationRights = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === WIRE_VALIDATION);
  }, [profile?.activityRights]);

  useEffect(() => {
    fetchWireValidationList();
    fetchMarketPlaceServices();
  }, []);

  useEffect(() => {
    setRowData(normalizeWireValidationRows(fetchWireValidationListResults ?? []));
  }, [fetchWireValidationListResults]);

  useEffect(() => {
    if (fetchMarketPlaceServicesResults) {
      const wireProduct = fetchMarketPlaceServicesResults.find(
        (p: MarketplaceProduct) => p.productCode === WIRE_VALIDATION_PRODUCT_CODE,
      );
      setIsWireValidationEnabled(wireProduct?.isEnabled ?? false);
    }
  }, [fetchMarketPlaceServicesResults]);

  useEffect(() => {
    if (getWireValidationOrderDetailStatus === AsyncStatus.SUCCESS && getWireValidationOrderDetailResults) {
      if (orderDetailIntentRef.current === 'copyCreate') {
        const detail = getWireValidationOrderDetailResults;
        setDrawerPrefilledValue({
          underwriterCode: detail.underwriter,
          fileNumber: detail.fileNumber,
          wireType: detail.wireType,
          firstName: detail.firstName,
          lastName: detail.lastName,
          receivingBankName: detail.bankName,
          wireAmount: detail.wireAmount,
          routingNumber: detail.routingNumber,
          payeeName: detail.payee,
          firmId: '',
        });
        setShowOrderDetailResult(false);
      } else {
        setShowOrderDetailResult(true);
      }
    }
  }, [getWireValidationOrderDetailStatus, getWireValidationOrderDetailResults]);

  const onFilterChanged = () => {
    if (!gridRef.current?.api) return;
    syncNoRowsOverlay(gridRef.current.api);
    queueMicrotask(() => {
      if (gridRef.current?.api) setDisplayedRowCount(gridRef.current.api.getDisplayedRowCount());
    });
  };

  const onModelUpdated = useCallback(() => {
    if (gridRef.current?.api) setDisplayedRowCount(gridRef.current.api.getDisplayedRowCount());
  }, []);

  const handleNewValidation = () => {
    setDrawerPrefilledValue(undefined);
    resetSubmitWireValidation();
    setIsDrawerOpen(true);
  };

  useEffect(() => {
    if (submitWireValidationStatus === AsyncStatus.SUCCESS) {
      hasSubmittedRef.current = true;
    }
  }, [submitWireValidationStatus]);

  const handleCopyCreateNew = (formData: Record<string, unknown>) => {
    resetSubmitWireValidation();
    setShowOrderDetailResult(false);
    setDrawerPrefilledValue(formData);
  };

  const handleCloseDrawer = () => {
    const shouldRefresh = hasSubmittedRef.current;
    setIsDrawerOpen(false);
    resetSubmitWireValidation();
    setShowOrderDetailResult(false);
    hasSubmittedRef.current = false;
    if (shouldRefresh) {
      fetchWireValidationList();
    }
  };

  const handlePayeeClick = useCallback(
    (row: WireValidationRow) => {
      orderDetailIntentRef.current = 'view';
      setIsDrawerOpen(true);
      getWireValidationOrderDetail(row.fileId, row.orderId);
    },
    [getWireValidationOrderDetail],
  );

  const handleCopyCreate = useCallback(
    (row: WireValidationRow) => {
      orderDetailIntentRef.current = 'copyCreate';
      resetSubmitWireValidation();
      setDrawerPrefilledValue(undefined);
      setIsDrawerOpen(true);
      getWireValidationOrderDetail(row.fileId, row.orderId);
    },
    [getWireValidationOrderDetail, resetSubmitWireValidation],
  );

  const PayeeRenderer = useCallback(
    (props: ICellRendererParams) => {
      const { data, node } = props;
      return (
        <Typography
          variant="body2"
          color="primary"
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => handlePayeeClick(data)}
          data-testid={`wire-validation-link-payee-or-bankname-${node.rowIndex ?? 0}`}
        >
          {data.payeeOrBankName}
        </Typography>
      );
    },
    [handlePayeeClick],
  );

  const handleReportClick = useCallback(
    async (fileId: string, orderId: string, payeeOrBankName: string) => {
      try {
        setLoadingReportFileId(fileId);
        setLoadingReportOrderId(orderId);

        const token = await getAccessToken();
        const pdfResponse = await getMarketplaceOrderReport(fileId, orderId, token, true);

        const fileName = `Wire Validation ${payeeOrBankName}.pdf`;
        openDocument(pdfResponse.Result.Data, DocumentType, newWindow, '', fileName);
      } catch (error) {
        getApiErrorMessages(error).forEach((message) => {
          addGlobalMsg({
            message,
            type: 'error',
          });
        });
      } finally {
        setLoadingReportFileId(null);
        setLoadingReportOrderId(null);
      }
    },
    [getAccessToken, addGlobalMsg],
  );

  const ActionsRenderer = useCallback(
    (props: ActionsCellRendererProps) => {
      const { data, loadingReportFileId, loadingReportOrderId, onReportClick, node } = props;
      const isLoadingThisReport = loadingReportFileId === data.fileId && loadingReportOrderId === data.orderId;

      const handleCopyCreateClick = () => handleCopyCreate(data);
      const handleViewReportClick = () => onReportClick(data.fileId, data.orderId, data.payeeOrBankName);

      return (
        <Box className={classes.actionsCell}>
          <Box className={classes.actionSlot}>
            {hasWireValidationRights && (
              <Tooltip title="Copy & Create New" arrow placement="left">
                <IconButton
                  size="small"
                  onClick={handleCopyCreateClick}
                  data-testid={`wire-validation-link-copy-create-${node.rowIndex ?? 0}`}
                >
                  <CopyCreateIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box className={classes.actionSlot}>
            {isLoadingThisReport ? (
              <CircularProgress size={16} />
            ) : (
              <Tooltip title="Wire Validation Report" arrow placement="left">
                <IconButton
                  size="small"
                  onClick={handleViewReportClick}
                  data-testid={`wire-validation-link-report-${node.rowIndex ?? 0}`}
                >
                  <PDFIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      );
    },
    [classes.actionsCell, classes.actionSlot, handleCopyCreate, hasWireValidationRights],
  );

  const actionsCellRendererParams = useMemo(
    () => ({
      loadingReportFileId,
      loadingReportOrderId,
      onReportClick: handleReportClick,
    }),
    [loadingReportFileId, loadingReportOrderId, handleReportClick],
  );

  const columnDefs = useMemo<ColDef[]>(() => {
    return wireValidationColDefs.map((col) =>
      col.field === 'actions' ? { ...col, cellRendererParams: actionsCellRendererParams } : col,
    );
  }, [actionsCellRendererParams]);

  const getDisplayedRowCountText = (): string => {
    const displayedCount = displayedRowCount ?? 0;
    const totalCount = fetchWireValidationListResults?.length ?? 0;
    return `Validations: ${displayedCount} of ${totalCount}`;
  };

  const doValidationsExist = rowData && rowData.length > 0;

  const CustomOverlayLoading = () => (
    <div className="files-container">
      <div className="no-rows">
        <LoadingSpinner status="pending" className="files-container--spinner-root" />
      </div>
    </div>
  );

  if (fetchWireValidationListStatus === AsyncStatus.PENDING) return <CustomOverlayLoading />;

  return (
    <ContentContainer>
      <div className={classes.contentWrap} data-testid="wire-validation-page">
        <PageHeader
          disableMargin="x"
          title="Wire Validation"
          subtitle={WIRE_VALIDATION_SUBTITLE}
          contentRight={
            doValidationsExist ? (
              <div className={classes.searchWrap}>
                <AgentNetButton
                  onClick={handleNewValidation}
                  color="primary"
                  variant="contained"
                  data-testid="wire-validation-new-validation-button-top"
                  size="small"
                  className={classes.searchButton}
                  plusIcon
                  disabled={!hasWireValidationRights}
                >
                  New Validation
                </AgentNetButton>
              </div>
            ) : undefined
          }
          divider={false}
          menuItems={[{ label: 'Knowledge Base', link: KBLinks.wireValidation }]}
        />
        {doValidationsExist ? (
          <div className={`ag-theme-alpine ${classes.gridContainer}`} data-testid="wire-validation-table">
            <DataTable
              ref={gridRef}
              components={{
                payeeRenderer: PayeeRenderer,
                verificationChip: VerificationChip,
                actionsRenderer: ActionsRenderer,
              }}
              onFilterChanged={onFilterChanged}
              rowData={rowData as WireValidationRow[] & Record<string, unknown>[]}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection={'multiple'}
              gridOptions={{
                suppressRowClickSelection: true,
                icons: {
                  filter: '<span class="ag-icon ag-icon-filter-custom"></span>',
                },
              }}
              animateRows={true}
              onModelUpdated={onModelUpdated}
            />
          </div>
        ) : (
          <WireVerificationEmptyState
            isWireValidationEnabled={isWireValidationEnabled}
            hasWireValidationRights={hasWireValidationRights}
            agentNetAdmin={agentNetAdmin}
            onNewValidation={handleNewValidation}
          />
        )}
        {doValidationsExist && (
          <Box py={2}>
            <Typography color="textPrimary" variant="body2" data-testid="wire-validation-row-count">
              {getDisplayedRowCountText()}
            </Typography>
          </Box>
        )}
        <WireVerificationDrawer
          openDrawer={isDrawerOpen}
          closeDrawer={handleCloseDrawer}
          value={drawerPrefilledValue}
          profileFirmId={userFirm?.firmId}
          onSubmitValidation={doSubmitWireValidation}
          submitStatus={submitWireValidationStatus}
          submitResult={
            showOrderDetailResult && getWireValidationOrderDetailResults
              ? getWireValidationOrderDetailResults
              : submitWireValidationResult
          }
          onCopyCreateNew={handleCopyCreateNew}
          orderDetailStatus={getWireValidationOrderDetailStatus}
          hasWireValidationRights={hasWireValidationRights}
          onViewReport={handleReportClick}
          loadingReportOrderId={loadingReportOrderId}
        />
      </div>
    </ContentContainer>
  );
};

export default WireVerification;
